/*
 * PER-1191 — shared instrumentation for the cross-engine memory
 * profile harness (viewer/tests/memory-profile.spec.ts). Used by both
 * worker.ts and main.ts so "how do we count triangles" and "when do
 * we emit a checkpoint line" have one definition, not two that can
 * drift apart.
 */

export interface MemProfileCheckpoint {
  step: string;
  memoryBytes: number | null;
  memoryMetric: string | null;
  ts: number | null;
  fileBytes: number | null;
  glbBytes: number | null;
  elementCount: number | null;
  triangleCount: number | null;
}

// Opt-in via `?memProfile=1` — without it, a published viewer never
// writes `[mem-profile]` lines to a real visitor's console. The
// Playwright spec passes the query param explicitly.
export const MEM_PROFILE_ENABLED =
  typeof location !== 'undefined' && new URLSearchParams(location.search).has('memProfile');

export function logMemProfileCheckpoint(checkpoint: MemProfileCheckpoint): void {
  if (!MEM_PROFILE_ENABLED) return;
  // eslint-disable-next-line no-console
  console.log(`[mem-profile] ${JSON.stringify(checkpoint)}`);
}

// glTF is a JSON chunk (metadata, including per-primitive accessor
// counts) followed by a binary chunk. We only need the JSON chunk
// header to get triangle counts — no need to touch the vertex data.
export function countGlbTriangles(glb: Uint8Array): number | null {
  try {
    const view = new DataView(glb.buffer, glb.byteOffset, glb.byteLength);
    if (glb.byteLength < 20 || view.getUint32(0, true) !== 0x46546c67 /* 'glTF' */) {
      return null;
    }
    const totalLength = view.getUint32(8, true);
    let offset = 12;
    type GltfJson = {
      meshes?: Array<{
        primitives?: Array<{ mode?: number; indices?: number; attributes?: { POSITION?: number } }>;
      }>;
      accessors?: Array<{ count?: number }>;
    };
    let json: GltfJson | null = null;
    while (offset + 8 <= totalLength) {
      const chunkLength = view.getUint32(offset, true);
      const chunkType = view.getUint32(offset + 4, true);
      const chunkStart = offset + 8;
      if (chunkType === 0x4e4f534a /* 'JSON' */) {
        const jsonBytes = glb.subarray(chunkStart, chunkStart + chunkLength);
        json = JSON.parse(new TextDecoder().decode(jsonBytes)) as GltfJson;
        break;
      }
      offset = chunkStart + chunkLength;
    }
    if (!json?.meshes || !json.accessors) return null;
    const accessors = json.accessors;
    let triangles = 0;
    for (const mesh of json.meshes) {
      for (const prim of mesh.primitives ?? []) {
        if (prim.mode !== undefined && prim.mode !== 4 /* TRIANGLES */) continue;
        const accessorIdx = prim.indices ?? prim.attributes?.POSITION;
        const count = accessorIdx === undefined ? undefined : accessors[accessorIdx]?.count;
        if (typeof count === 'number') triangles += Math.floor(count / 3);
      }
    }
    return triangles;
  } catch {
    return null;
  }
}
